interface GoalsAddUpdateConfig {
    ajaxUrl: string;
    homeUrl: string;
    myUpdatesUrl: string;
}

interface AjaxResponse {
    success: boolean;
    data: {
        content: string;
        timestamp: string;
        primary_goal?: boolean;
        message?: string;
    };
}

function formatClockDate(d: Date): string {
    const dateStr = d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' });
    const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
    return dateStr + ' ' + timeStr;
}

function escHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function buildSuccessHtml(content: string, timestamp: string, homeUrl: string, myUpdatesUrl: string, primaryGoal: boolean): string {
    const waSuffix = primaryGoal ? 'Goals ✓' : 'Goals';
    const fullText = content + '\n_\n' + waSuffix;
    const waLink = 'https://wa.me/?text=' + encodeURIComponent(fullText);
    return `
    <div id="add-updates-success-screen" class="add-updates-success add-updates-success--enter">
        <div class="add-updates-success__card">
            <div class="add-updates-success__header">
                <svg class="add-updates-success__icon" width="48" height="48" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M8 12l3 3 5-5"/>
                </svg>
                <h2 class="add-updates-success__title">Update Posted!</h2>
            </div>
            <div class="add-updates-success__timestamp">${escHtml(timestamp)}</div>
            <div id="add-updates-success-message" class="add-updates-success__message">${escHtml(fullText)}</div>
            <div class="add-updates-success__actions">
                <a id="add-updates-whatsapp-btn" href="${waLink}" target="_blank" rel="noopener"
                    class="btn btn-success add-updates-btn-whatsapp">
                    <span>Post to WhatsApp</span>
                    <svg class="add-updates-btn-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                </a>
                <button type="button" id="add-updates-copy-btn" class="btn btn-primary add-updates-btn-copy">
                    <span class="add-updates-btn-copy-text">Copy Text</span>
                    <svg class="add-updates-btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                </button>
            </div>
        </div>
        <div class="add-updates-success__footer">
            <a href="${escHtml(homeUrl)}" class="btn btn-primary-outline btn-sm">Dashboard</a>
            <a href="${escHtml(myUpdatesUrl)}" class="btn btn-secondary-outline btn-sm">My Updates</a>
        </div>
    </div>`;
}

function wireCopyBtn(content: string): void {
    const copyBtn = document.getElementById('add-updates-copy-btn');
    if (!copyBtn) return;
    copyBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(content).then(() => {
            const span = copyBtn.querySelector('.add-updates-btn-copy-text');
            if (span) {
                const orig = span.textContent || '';
                span.textContent = 'Copied!';
                setTimeout(() => {
                    span.textContent = orig;
                }, 2000);
            }
        });
    });
}

export function addUpdates(): void {
    if (!document.querySelector('.add-updates')) return;

    const clockEl = document.getElementById('add-updates-clock');
    if (clockEl) {
        const tick = () => {
            clockEl.textContent = formatClockDate(new Date());
        };
        tick();
        setInterval(tick, 1000);
    }

    const form = document.getElementById('add-updates-form') as HTMLFormElement | null;
    if (!form) return;

    const config = (window as Window & { goalsAddUpdate?: GoalsAddUpdateConfig }).goalsAddUpdate;
    if (!config) return;

    type ParsleyLike = { validate: () => boolean };
    type JqLike = (s: string) => { parsley: () => ParsleyLike };
    const $jq = (window as unknown as { $j?: JqLike }).$j;
    if ($jq) $jq('#add-updates-form').parsley();

    form.addEventListener('submit', (e: Event) => {
        e.preventDefault();
        if ($jq && !$jq('#add-updates-form').parsley().validate()) return;
        void handleSubmit(form, config);
    });
}

function resetForm(
    btn: HTMLButtonElement,
    origHtml: string,
    textarea: HTMLTextAreaElement,
    primaryCheckbox: HTMLInputElement | null,
    privateCheckbox: HTMLInputElement | null,
): void {
    btn.disabled = false;
    btn.innerHTML = origHtml;
    textarea.disabled = false;
    if (primaryCheckbox) primaryCheckbox.disabled = false;
    if (privateCheckbox) privateCheckbox.disabled = false;
}

async function handleSubmit(form: HTMLFormElement, config: GoalsAddUpdateConfig): Promise<void> {
    const textarea = form.querySelector('textarea[name="content"]') as HTMLTextAreaElement | null;
    if (!textarea?.value.trim()) {
        showError('Please enter your update.');
        return;
    }

    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const btnOrigHtml = btn.innerHTML;
    const primaryCheckbox = form.querySelector('input[name="primary_goal"]') as HTMLInputElement | null;
    const privateCheckbox = form.querySelector('input[name="private"]') as HTMLInputElement | null;

    const startTime = Date.now();
    const formData = new FormData(form);

    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span><span>Saving…</span>';
    textarea.disabled = true;
    if (primaryCheckbox) primaryCheckbox.disabled = true;
    if (privateCheckbox) privateCheckbox.disabled = true;
    formData.set('action', 'goals_add_update');

    try {
        const res = await fetch(config.ajaxUrl, { method: 'POST', body: formData });
        const data = (await res.json()) as AjaxResponse;

        const elapsed = Date.now() - startTime;
        await new Promise<void>(r => setTimeout(r, Math.max(0, 1500 - elapsed)));

        if (data.success) {
            transitionToSuccess(data.data.content, data.data.timestamp, config.homeUrl, config.myUpdatesUrl, data.data.primary_goal ?? false);
        } else {
            showError(data.data?.message || 'Something went wrong. Please try again.');
            resetForm(btn, btnOrigHtml, textarea, primaryCheckbox, privateCheckbox);
        }
    } catch {
        showError('Something went wrong. Please try again.');
        resetForm(btn, btnOrigHtml, textarea, primaryCheckbox, privateCheckbox);
    }
}

function showError(msg: string): void {
    const el = document.getElementById('add-updates-messages');
    if (el) el.innerHTML = `<p class="error">${escHtml(msg)}</p>`;
}

function transitionToSuccess(content: string, timestamp: string, homeUrl: string, myUpdatesUrl: string, primaryGoal: boolean): void {
    const formWrap = document.getElementById('add-updates-form-wrap');
    const successWrap = document.getElementById('add-updates-success-wrap');
    if (!formWrap || !successWrap) return;

    formWrap.classList.add('slide-out');

    setTimeout(() => {
        formWrap.style.display = 'none';

        const form = formWrap.querySelector('#add-updates-form') as HTMLFormElement | null;
        if (form) {
            const textarea = form.querySelector('textarea[name="content"]') as HTMLTextAreaElement | null;
            const primaryCheckbox = form.querySelector('input[name="primary_goal"]') as HTMLInputElement | null;
            const privateCheckbox = form.querySelector('input[name="private"]') as HTMLInputElement | null;
            const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
            const messages = document.getElementById('add-updates-messages');
            if (textarea) {
                textarea.value = '';
                textarea.disabled = false;
            }
            if (primaryCheckbox) {
                primaryCheckbox.checked = false;
                primaryCheckbox.disabled = false;
            }
            if (privateCheckbox) {
                privateCheckbox.checked = false;
                privateCheckbox.disabled = false;
            }
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = 'Post Update';
            }
            if (messages) messages.innerHTML = '';
        }

        successWrap.innerHTML = buildSuccessHtml(content, timestamp, homeUrl, myUpdatesUrl, primaryGoal);
        successWrap.style.display = '';

        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                successWrap.querySelector('.add-updates-success')?.classList.remove('add-updates-success--enter');
            }),
        );

        const waSuffix = primaryGoal ? 'Goals ✓' : 'Goals';
        wireCopyBtn(content + '\n_\n' + waSuffix);
    }, 350);
}
